/**
 * Generic traversal over the IR.
 *
 * The assembler, the renderers, text extraction and revision diffing all
 * need to walk the same tree. Doing it once here — rather than in each
 * consumer — is what keeps "add a node type" a single-file change.
 */
/** Replaces every inline node in a block subtree, bottom-up. */
export function mapInlines(nodes, fn) {
    const mapInline = (n) => {
        const mapped = fn(n);
        if (mapped.type === 'strong' || mapped.type === 'emphasis' || mapped.type === 'label') {
            return { ...mapped, content: mapped.content.map(mapInline) };
        }
        return mapped;
    };
    const mapList = (list) => list.map(mapInline);
    return nodes.map((node) => {
        switch (node.type) {
            case 'section':
                return { ...node, content: mapInlines(node.content, fn) };
            case 'heading':
            case 'paragraph':
                return { ...node, content: mapList(node.content) };
            case 'list':
                return { ...node, items: node.items.map((i) => ({ ...i, content: mapInlines(i.content, fn) })) };
            case 'optionList':
                return {
                    ...node,
                    items: node.items.map((i) => ({
                        ...i,
                        content: mapList(i.content),
                        ...(i.children ? { children: mapInlines(i.children, fn) } : {}),
                    })),
                };
            case 'table':
                return {
                    ...node,
                    columns: node.columns.map((c) => (c.header ? { ...c, header: mapList(c.header) } : c)),
                    rows: node.rows.map((r) => ({
                        ...r,
                        cells: r.cells.map((c) => ({ ...c, content: mapInlines(c.content, fn) })),
                    })),
                    ...(node.caption ? { caption: mapList(node.caption) } : {}),
                };
            case 'detailList':
                return {
                    ...node,
                    items: node.items.map((i) => ({ ...i, term: mapList(i.term), description: mapInlines(i.description, fn) })),
                };
            case 'signature':
                return node.statement ? { ...node, statement: mapList(node.statement) } : node;
            case 'notice':
            case 'group':
                return { ...node, content: mapInlines(node.content, fn) };
            default:
                return node;
        }
    });
}
/** Every block node in the subtree, in document order (parents first). */
export function walkBlocks(nodes, visit, ancestors = []) {
    for (const node of nodes) {
        visit(node, ancestors);
        const next = [...ancestors, node];
        switch (node.type) {
            case 'section':
            case 'notice':
            case 'group':
                walkBlocks(node.content, visit, next);
                break;
            case 'list':
                for (const i of node.items)
                    walkBlocks(i.content, visit, next);
                break;
            case 'optionList':
                for (const i of node.items)
                    if (i.children)
                        walkBlocks(i.children, visit, next);
                break;
            case 'table':
                for (const r of node.rows)
                    for (const c of r.cells)
                        walkBlocks(c.content, visit, next);
                break;
            case 'detailList':
                for (const i of node.items)
                    walkBlocks(i.description, visit, next);
                break;
            default:
                break;
        }
    }
}
/** Every inline node in the subtree, in document order. */
export function walkInlines(nodes, visit) {
    mapInlines(nodes, (n) => {
        visit(n);
        return n;
    });
}
/** Direct child sections of a node list, at one level. */
export function childSections(nodes) {
    return nodes.filter((n) => n.type === 'section');
}
/**
 * Flattens a subtree to plain text. Useful for search indexing, for
 * diffing two revisions, and for a fail-loud check that a compiled
 * document actually says what a snapshot test expects.
 */
export function plainText(nodes) {
    const out = [];
    const inlineText = (n) => {
        switch (n.type) {
            case 'text':
                return n.text;
            case 'strong':
            case 'emphasis':
            case 'label':
                return n.content.map(inlineText).join('');
            case 'lineBreak':
                return '\n';
            case 'value':
                return n.text ?? '____';
            case 'image':
                return '';
            default:
                return n.text ?? '';
        }
    };
    const blockText = (list) => {
        for (const node of list) {
            switch (node.type) {
                case 'section':
                    out.push([node.number ? `§ ${node.number}` : null, node.title].filter(Boolean).join(' '));
                    blockText(node.content);
                    break;
                case 'heading':
                case 'paragraph':
                    out.push(node.content.map(inlineText).join(''));
                    break;
                case 'list':
                    for (const i of node.items)
                        blockText(i.content);
                    break;
                case 'optionList':
                    for (const i of node.items) {
                        out.push(`${i.checked ? '[x]' : '[ ]'} ${i.content.map(inlineText).join('')}`);
                        if (i.children)
                            blockText(i.children);
                    }
                    break;
                case 'table':
                    for (const r of node.rows)
                        for (const c of r.cells)
                            blockText(c.content);
                    break;
                case 'detailList':
                    for (const i of node.items) {
                        out.push(`${i.term.map(inlineText).join('')}:`);
                        blockText(i.description);
                    }
                    break;
                case 'notice':
                case 'group':
                    if (node.title)
                        out.push(node.title);
                    blockText(node.content);
                    break;
                case 'signature':
                    for (const panel of node.panels)
                        out.push(panel.label);
                    break;
                case 'attachmentList':
                    for (const e of node.entries ?? [])
                        out.push(`${e.number != null ? `Bilaga ${e.number}: ` : ''}${e.label}`);
                    break;
                default:
                    break;
            }
        }
    };
    blockText(nodes);
    return out.filter((l) => l.trim() !== '').join('\n');
}
//# sourceMappingURL=walk.js.map